import { AppError, toAppError } from "../types/errors"; // Normalized AppError
import { useAuthStore } from "@store"; // For setting auth errors
import * as Sentry from "@sentry/react"; // Added Sentry
import { logDebug, logError, logWarn } from "./logging"; // Import logging utilities

export async function processSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal: AbortSignal,
  onMessage: (data: string, eventType?: string) => void,
  onEnd: () => void,
  onError: (error: AppError) => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      if (signal.aborted) {
        logDebug("processSSEStream: Aborted.");
        // UMW-FIX: Consider calling onError or onEnd if aborted during processing,
        // or let the caller handle the abort signal's aftermath.
        // For now, just break. The caller's finally block should handle cleanup.
        break;
      }

      const { value, done } = await reader.read();

      if (done) {
        if (buffer.trim()) {
          logWarn(
            "processSSEStream: Stream ended with unprocessed buffer content:",
            buffer,
          );
          // Potentially parse this buffer if it could contain a final partial event
        }
        onEnd();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      let eolIndex;

      while ((eolIndex = buffer.indexOf("\n\n")) !== -1) {
        const eventString = buffer.substring(0, eolIndex);
        buffer = buffer.substring(eolIndex + 2);

        let eventType = "";
        let eventData = "";
        const lines = eventString.split("\n");

        for (const line of lines) {
          if (line.startsWith("event:")) {
            eventType = line.substring("event:".length).trim();
          } else if (line.startsWith("data:")) {
            // UMW-Debug: Log raw line and extracted data
            logDebug(`[sseUtils] processSSEStream: Raw data line: "${line}"`);
            const extractedData = line.substring("data:".length).trim();
            logDebug(
              `[sseUtils] processSSEStream: Extracted data string part: "${extractedData}"`,
            );
            eventData += extractedData; // Continue appending if data is split across multiple data: lines for the same event
          }
        }

        if (eventData) {
          // UMW-Debug: Log before calling onMessage
          logDebug(
            `[sseUtils] processSSEStream: FINAL eventData BEFORE onMessage. EventType: "${eventType}", EventData: "${eventData}"`,
          );
          if (eventType === "error") {
            logError('processSSEStream: Received "error" event:', eventData);
            let appErr: AppError;
            try {
              const parsed = JSON.parse(eventData);
              appErr = toAppError(parsed, 500);
            } catch {
              appErr = toAppError(eventData, 500);
            }
            onError(appErr);
          } else if (eventType === "end") {
            onMessage(eventData, eventType); // Send data from 'end' event if any
            onEnd(); // Then call onEnd
          } else {
            onMessage(eventData, eventType || undefined);
          }
        } else if (eventString.startsWith("data:")) {
          // Handle unnamed events with only data line
          const singleLineData = eventString.substring("data:".length).trim();
          // UMW-Debug: Log for single line data
          logDebug(
            `[sseUtils] processSSEStream: Single line data (unnamed event), FINAL singleLineData BEFORE onMessage: "${singleLineData}"`,
          );
          onMessage(singleLineData);
        }
      }
    }
  } catch (readError) {
    if (signal.aborted) {
      logDebug("processSSEStream: Read aborted by signal.");
    } else {
      logError("processSSEStream: Error reading from stream:", readError);
      onError(toAppError(readError, 500));
    }
  }
  // The finally block for reader.cancel/releaseLock should be in the caller function
  // that creates the reader and the AbortController.
}

/**
 * A utility to make a fetch request for SSE and then process the stream.
 * This combines the fetch setup and the stream processing.
 */
export async function fetchAndProcessSSE<
  TRequestBody = Record<string, unknown>,
>({
  url,
  method = "POST",
  body,
  signal,
  onOpen,
  onMessage,
  onEnd,
  onError,
}: {
  url: string;
  method?: "GET" | "POST";
  body?: TRequestBody;
  signal: AbortSignal; // Make signal mandatory for cancellation
  onOpen?: () => void;
  onMessage: (data: string, eventType?: string) => void;
  onEnd: () => void;
  onError: (error: AppError) => void;
}): Promise<ReadableStreamDefaultReader<Uint8Array> | null> {
  // Return reader for cancellation by caller if needed
  const { jwt } = useAuthStore.getState();
  const headers: HeadersInit = { Accept: "text/event-stream" };
  if (jwt) headers["Authorization"] = `Bearer ${jwt}`;
  if (method === "POST") headers["Content-Type"] = "application/json";

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: method === "POST" && body ? JSON.stringify(body) : undefined,
      signal,
    });

    if (signal.aborted) {
      logDebug("fetchAndProcessSSE: Aborted before response.");
      return null;
    }

    if (!response.ok || !response.body) {
      let errorData: unknown = { detail: "Failed to connect to event stream." };
      try {
        errorData = await response.json();
      } catch {
        // keep default
      }

      const errorMessage =
        typeof (errorData as { detail?: unknown })?.detail === "string"
          ? (errorData as { detail: string }).detail
          : "SSE initial connection failed";

      if (import.meta.env.PROD) {
        Sentry.captureMessage(`${errorMessage}`, {
          contexts: {
            fetch_error: {
              status: response.status,
              url: url,
              response_body: errorData,
              method: method,
            },
          },
          level: "error",
        });
      }

      const appErr = toAppError(errorData, response.status);
      if (appErr.status === 401 || appErr.status === 403) {
        useAuthStore.getState().setAuthError(appErr.message);
      } else if (appErr.status === 429) {
        useAuthStore.getState().setRateLimitError(true, appErr.message);
      }
      throw appErr;
    }

    onOpen?.();
    reader = response.body.getReader();
    await processSSEStream(reader, signal, onMessage, onEnd, onError);
    return reader; // Return reader so it can be cancelled/released by caller if needed
  } catch (err) {
    if (signal.aborted) {
      logDebug("fetchAndProcessSSE: Fetch aborted by signal.");
    } else {
      logError("fetchAndProcessSSE: Connection error:", err);
      if (import.meta.env.PROD) {
        Sentry.captureException(err, {
          // Added Sentry for initial fetch/connection errors
          tags: {
            utility: "sseUtils",
            method: "fetchAndProcessSSE",
            stage: "initial_fetch",
          },
          extra: { url: url, method: method },
        });
      }
      onError(toAppError(err, 500));
    }
    return null;
  }
  // The caller should have a finally block to release the reader if it was returned.
}
